import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActionType, ArticlePath, createGuideLink, TextKey } from '@mews-commander/enums';
import { ModalWrapper, NewModalBodyWrapper, OverlayedLoader } from '@mews-commander/ui';
import { LocalizedNumber, LocalizedText, lt, useFeatureFlag, useGainsightService } from '@mews/framework';
import { Stack, Typography } from '@optimus-web/core';
import { IconName } from '@optimus-web/icons';
import { HeadingAction } from '../../../legacy';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { AccountMergeContentWrapper } from './AccountMergeContentWrapper';
import { AccountMergeEmptyContent } from './AccountMergeEmptyContent';
import { getGainSightEventObject, MatchAndMergeGainSightEvents } from './GainSightEvents';
import { useAccountMergeState, useMatchedProfiles } from './hooks';
import { setTotalMatchingGuestProfileHistory, updateTotalMatchingGuestProfileHistory } from './store/accountMergeStore';
import { MergeMode } from './types';

export const AccountMerge = () => {
    const [openEmptyPage, setOpenEmptyPage] = useState<boolean>(false);
    /** Refetching data as a workaround for a Backend bug not returning proper data */
    const [refetchingData, setRefetchingData] = useState<boolean>(false);
    const { guestProfileIds, mergeMode, totalProfileMatches, cursor, previousMatchId } = useAccountMergeState();
    const gainSightService = useGainsightService();
    const guideLink = createGuideLink(ArticlePath.MergeCustomerProfiles);

    const newMatchQueryEnabled = useFeatureFlag('newMatchQueryEnabled');

    const {
        profiles: matchedGuestProfiles,
        loading,
        matchedProfilesWithPagination,
        refetch,
    } = useMatchedProfiles({
        cursor,
        guestProfileIds,
        mergeMode,
        newMatchQueryEnabled,
        previousMatchId,
        totalProfileMatches,
    });

    /** Gainsight event Tracking Start */
    useEffect(() => {
        /** These conditions prevent duplicate calls for gain sight services */
        if (!openEmptyPage && !loading && !refetchingData && matchedGuestProfiles?.profiles?.length) {
            gainSightService.trackEvent(
                MatchAndMergeGainSightEvents.MatchAndMergeDisplayed,
                getGainSightEventObject(matchedGuestProfiles, mergeMode)
            );
        }
    }, [gainSightService, loading, openEmptyPage, matchedGuestProfiles, mergeMode, refetchingData]);

    /** Gainsight event Tracking End */

    const actions: HeadingAction[] = [
        {
            url: guideLink,
            target: '_blank',
            icon: IconName.Help,
            text: <LocalizedText textKey={TextKey.Help} />,
            type: ActionType.Link,
        },
    ];

    const title = useMemo(() => {
        const profileCount = openEmptyPage ? 0 : <LocalizedNumber number={totalProfileMatches} />;
        return (
            <Stack spacing="100" itemAlignment="center">
                <Stack>{lt('AccountMergePageTitle')}</Stack>
                {mergeMode === MergeMode.Queue ? (
                    <Typography textStyle="bodyMedium" as="div">
                        <LocalizedText
                            textKey={TextKey.AccountMergeTotalProfilesToReview}
                            substitutes={{ count: profileCount }}
                        />
                    </Typography>
                ) : null}
            </Stack>
        );
    }, [mergeMode, openEmptyPage, totalProfileMatches]);

    // To navigate to next match if any or show empty page
    const navigateToNextMatch = useCallback(
        async (usePrevious: boolean) => {
            const { cursor: currentCursor, pageInfo } = matchedProfilesWithPagination || {};

            if (pageInfo?.hasNextPage && currentCursor) {
                setOpenEmptyPage(false);
                updateTotalMatchingGuestProfileHistory({
                    totalProfileMatches: totalProfileMatches - 1,
                    cursor: usePrevious && totalProfileMatches - 1 ? cursor : currentCursor,
                });
                if (usePrevious && totalProfileMatches - 1) {
                    /** Refetching data as a workaround for a Backend bug not returning proper data */
                    setRefetchingData(true);
                    await refetch();
                } else {
                    setRefetchingData(false);
                }
            } else if (!pageInfo?.hasNextPage) {
                setRefetchingData(false);
                setOpenEmptyPage(true);
                setTotalMatchingGuestProfileHistory({ totalProfileMatches: 0 });
            }
        },
        [cursor, matchedProfilesWithPagination, refetch, totalProfileMatches]
    );

    const navigateToNextMatchNew = useCallback(() => {
        if (totalProfileMatches > 0) {
            setOpenEmptyPage(false);
            if (!matchedGuestProfiles) {
                setOpenEmptyPage(true);
                setTotalMatchingGuestProfileHistory({ totalProfileMatches: 0 });
            } else {
                updateTotalMatchingGuestProfileHistory({
                    totalProfileMatches: totalProfileMatches - 1,
                    previousMatchId: matchedGuestProfiles.id,
                });
            }
        }
    }, [totalProfileMatches, matchedGuestProfiles]);

    const bodyContent = useMemo(() => {
        if (openEmptyPage || (mergeMode === MergeMode.Queue && !totalProfileMatches)) {
            return <AccountMergeEmptyContent mergeMode={mergeMode} />;
        }

        return (
            <>
                <OverlayedLoader show={loading} />
                {!loading && matchedGuestProfiles ? (
                    <AccountMergeContentWrapper
                        matchedGuestProfiles={matchedGuestProfiles}
                        guideLink={guideLink}
                        mergeMode={mergeMode}
                        navigateToNextMatch={newMatchQueryEnabled ? navigateToNextMatchNew : navigateToNextMatch}
                    />
                ) : null}
            </>
        );
    }, [
        openEmptyPage,
        mergeMode,
        totalProfileMatches,
        loading,
        matchedGuestProfiles,
        guideLink,
        newMatchQueryEnabled,
        navigateToNextMatchNew,
        navigateToNextMatch,
    ]);

    return (
        <ModalWrapper>
            <ModalHeader title={title} actions={actions} />
            <NewModalBodyWrapper padded>{bodyContent}</NewModalBodyWrapper>
        </ModalWrapper>
    );
};
